import * as React from 'react';
import { useState } from 'react';
import { MdOutlineClose } from 'react-icons/md';
import Modal from 'components/Modal/HeadlessUiModal';
import useViewer from 'hooks/useViewer';
import { Timezone_Location_Enum } from 'types/generated/client';
import { useCurrentUser } from 'hooks/useCurrentUser';
import PermissionedBlock from 'components/PermissionedBlock';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import ModalSidebar from './ModalSidebar';
import OptionsFlowSection from './OptionsFlowSection';
import DarkPoolSection from './DarkPoolSection';
import PayoffSection from './PayoffSection';
import { calculateDirectionalFlowValue } from 'utils/shared/trades/aggregateOptionFlow';

import { useTickerData, usePayoffCalculation, useDarkPoolData } from './hooks';
import { useDynamicScrollSection } from 'hooks/useDynamicScrollSection';
import { MoreFeatureModalProps } from './types';
import { NAV_ITEMS } from './constants';
import { getDefaultFlowSideBasisSelection } from 'utils/shared/trades/presets';

const SECTION_IDS = NAV_ITEMS.map((item) => item.id);

const MoreFeatureModal: React.FC<MoreFeatureModalProps> = ({
  isOpen,
  onClose,
  trade,
  hasAccess,
  metricMode = 'total',
  flowSideBasis,
}) => {
  const viewer = useViewer();
  const { user: currentUser } = useCurrentUser();
  const timezoneLocation = currentUser?.timezoneLocation || Timezone_Location_Enum.Current;
  const isWallstreetTimezone = timezoneLocation === Timezone_Location_Enum.Wallstreet;

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);

  const ticker = trade?.ticker || undefined;
  const companyName = trade?.tickerDetails?.name || trade?.ticker || undefined;
  const sector = trade?.tickerDetails?.sector || undefined;
  const activeFlowSideBasis = React.useMemo(
    () => flowSideBasis || getDefaultFlowSideBasisSelection(),
    [flowSideBasis],
  );

  const { trades, sweepTrades, unusualTrades, dailyVolume, putFlow, loading } = useTickerData(
    ticker,
    isOpen,
    isWallstreetTimezone,
    activeFlowSideBasis,
  );
  const directionalPremium = React.useMemo(
    () => calculateDirectionalFlowValue(trades, (currentTrade) => currentTrade.premium || 0),
    [trades],
  );

  const {
    trades: darkPoolTrades,
    darkPoolByTicker,
    loading: darkPoolLoading,
  } = useDarkPoolData(ticker, isOpen);

  const {
    calculationData,
    spot,
    strike,
    premium,
    volatility,
    loading: payoffLoading,
  } = usePayoffCalculation(trade, isOpen);

  const darkPoolHasAccess = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);

  const { activeSection, scrollToSection } = useDynamicScrollSection({
    rootEl,
    sectionIds: SECTION_IDS,
  });

  if (!ticker || !trade) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      classNameMaxWidth={hasAccess ? 'w-full mx-auto max-w-[88rem]' : 'max-w-2xl mx-auto'}
      classNameHeight={hasAccess ? 'h-full' : ''}
    >
      {!hasAccess ? (
        <div className="bg-palette-black-3 py-8 text-white">
          <PermissionedBlock />
        </div>
      ) : (
        <div className="mx-2 my-2 flex h-[calc(100vh-2rem)] flex-col rounded-lg bg-palette-black-3 text-white md:mx-4 md:my-4 md:flex-row md:overflow-hidden">
          <div className="hidden md:block">
            <ModalSidebar
              ticker={ticker}
              companyName={companyName}
              sector={sector}
              navItems={NAV_ITEMS}
              activeSection={activeSection ?? ''}
              onSectionClick={scrollToSection}
              onClose={onClose}
              bullishPremium={directionalPremium.bullish}
              bearishPremium={directionalPremium.bearish}
              neutralPremium={directionalPremium.neutral}
            />
          </div>
          <button
            onClick={onClose}
            className="fixed right-6 top-6 z-50 rounded-full bg-palette-black-4/90 p-2 text-gray-400 shadow-lg backdrop-blur-sm transition-colors hover:bg-gray-700 hover:text-white md:hidden"
            aria-label="Close"
          >
            <MdOutlineClose className="h-6 w-6" />
          </button>
          <div className="flex h-full flex-1 flex-col overflow-hidden">
            <div
              ref={setRootEl}
              className="h-full flex-1 overflow-y-auto px-3 py-3 md:px-8 md:py-4"
            >
              <div className="max-w-full pb-2">
                <OptionsFlowSection
                  ticker={ticker}
                  trades={trades}
                  sweepTrades={sweepTrades}
                  unusualTrades={unusualTrades}
                  putFlow={putFlow}
                  dailyVolume={dailyVolume}
                  loading={loading}
                  viewer={viewer}
                  metricMode={metricMode}
                />
                <DarkPoolSection
                  ticker={ticker}
                  darkPoolTrades={darkPoolTrades}
                  darkPoolLoading={darkPoolLoading}
                  hasAccess={darkPoolHasAccess}
                  viewer={viewer}
                  darkPoolByTicker={darkPoolByTicker}
                />
                <PayoffSection
                  ticker={ticker}
                  calculationData={calculationData}
                  spot={spot}
                  strike={strike}
                  premium={premium}
                  volatility={volatility}
                  trade={trade}
                  loading={payoffLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MoreFeatureModal;
